import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import {
  ArrowRight, ArrowLeft, Building2, Users, CreditCard, Check, Upload,
  FileText, Loader2, CheckCircle2, Zap, Info,
  Mic, MessageSquare, Sparkles, ScanLine, BarChart3, Calendar,
  Hash, Plus, Trash2, AlertTriangle, RefreshCw, Phone, Table2, MapPin
} from 'lucide-react';
import SetupUploadLightbox from '../../components/shared/SetupUploadLightbox';
import { useAppMode } from '../../contexts/AppModeContext';

const ARIAONE_LOGO = "/ariaone_logo_black.png";
const LUMINA_LOGO = "https://customer-assets.emergentagent.com/job_revenue-cycle-1/artifacts/rms1wglh_lumina_logo_black.png";
const REVQ_LOGO = "https://customer-assets.emergentagent.com/job_medcycle-metrics/artifacts/zakm44og_revQ_logo_black.png";
const ECHO_LOGO = "https://customer-assets.emergentagent.com/job_medcycle-metrics/artifacts/r0ngtnwn_echo_logo_black.svg";
const APP_LOGOS = { lumina: LUMINA_LOGO, revq: REVQ_LOGO, echo: ECHO_LOGO };

/* ── Per-app document upload configs ── */
const APP_UPLOAD_DOCS = {
  lumina: {
    name: 'Lumina', logo: LUMINA_LOGO,
    docs: [
      { id: 'ccda', label: 'CCDA / CCD Export', desc: 'Continuity of Care Documents from your existing EHR', icon: FileText, shared: ['echo'], required: false },
      { id: 'patient_csv', label: 'Patient List (.csv)', desc: 'CSV with patient names, DOB, insurance, and contact info', icon: Users, shared: ['echo'], required: false },
      { id: 'appt_list', label: 'Appointment Schedule', desc: 'Daily or weekly appointment list from your EHR', icon: Calendar, shared: ['echo'], required: false },
    ],
  },
  revq: {
    name: 'RevQ', logo: REVQ_LOGO,
    docs: [
      { id: 'edi_837', label: 'EDI 837 Claim Batch', desc: '1-month batch preferred, but 1-week or any duration works. Professional (837P) or Institutional (837I).', extraDesc: 'An EDI 837 is the standard electronic file your billing software generates when submitting claims to insurance companies. It contains all the claim details — patient info, diagnosis codes, procedure codes, and charges — in a format payers can process automatically.', icon: BarChart3, shared: [], required: true },
      { id: 'edi_835', label: 'EDI 835 Remittance (ERA)', desc: 'Electronic Remittance Advice for payment reconciliation', icon: CreditCard, shared: [], required: false },
      { id: 'patient_balances', label: 'Patient Balances Report', desc: 'Aging report or patient balance export from your billing system', icon: FileText, shared: [], required: false },
      { id: 'fee_schedule', label: 'Fee Schedule', desc: 'Your contracted fee schedule for claims scrubbing', icon: Hash, shared: [], required: false },
    ],
    csvAlternatives: {
      label: "Don't have EDI files?",
      desc: 'You can upload CSV files instead. We will extract the same data from your spreadsheets.',
      docs: [
        { id: 'csv_insurance', label: 'Patient Insurance List (.csv)', desc: 'Member IDs, payer names, group numbers, copays', icon: Table2, required: false },
        { id: 'csv_demographics', label: 'Patient Demographics (.csv)', desc: 'Names, DOBs, addresses, phone numbers, guarantors', icon: Users, required: false },
        { id: 'csv_practice_report', label: 'Practice Report Details (.csv)', desc: 'Revenue summaries, encounter volumes, payer mix data', icon: BarChart3, required: false },
        { id: 'csv_physician', label: 'Physician Details (.csv)', desc: 'Provider names, NPIs, specialties, tax IDs, credentials', icon: FileText, required: false },
        { id: 'csv_service_location', label: 'Service Location (.csv)', desc: 'Facility names, addresses, place of service codes', icon: MapPin, required: false },
      ],
    },
  },
  echo: {
    name: 'Echo', logo: ECHO_LOGO,
    docs: [
      { id: 'appt_list_echo', label: 'Appointment Schedule', desc: 'Daily or weekly appointment list — automatically shared with Lumina if active', icon: Calendar, shared: ['lumina'], required: false },
      { id: 'calendar_export', label: 'Calendar Export (.ics / .csv)', desc: 'Provider calendars with availability and blocked times', icon: Calendar, shared: [], required: false },
      { id: 'patient_csv_echo', label: 'Patient Roster (.csv)', desc: 'Demographics roster — shared across Lumina if active', icon: Users, shared: ['lumina'], required: false },
    ],
  },
};

/* ── Lens processing results mock ── */
const LENS_RESULTS = {
  edi_837: { fields: [{ f: 'Claims Found', v: '847 claims (Jan 2026)', c: 98 }, { f: 'Unique Patients', v: '312 patients', c: 97 }, { f: 'Total Billed', v: '$1,247,830.00', c: 99 }, { f: 'Top CPT Codes', v: '99213, 99214, 99215, 99203', c: 95 }, { f: 'Payer Mix', v: 'BCBS 34%, Medicare 28%, Aetna 18%, UHC 12%', c: 94 }] },
  ccda: { fields: [{ f: 'Patient', v: 'John Smith', c: 99 }, { f: 'Problems', v: 'E11.9, I10, E78.5', c: 96 }, { f: 'Medications', v: '3 active', c: 95 }, { f: 'Allergies', v: 'Penicillin, Sulfa', c: 97 }] },
  patient_csv: { fields: [{ f: 'Records', v: '245 patients loaded', c: 98 }, { f: 'Fields', v: 'Name, DOB, Phone, Insurance, PCP', c: 96 }] },
  appt_list: { fields: [{ f: 'Date Range', v: 'Mar 24-28, 2026', c: 99 }, { f: 'Appointments', v: '124 total', c: 97 }, { f: 'Providers', v: '4 providers', c: 96 }] },
};

/* ── Credit pricing ── */
const CREDIT_PRICING = [
  { icon: Mic, label: 'Ambient Listening', rate: '1 credit / min', color: 'bg-violet-50 text-violet-600', app: 'Lumina' },
  { icon: MessageSquare, label: 'Dictation', rate: '1 credit / min', color: 'bg-blue-50 text-blue-600', app: 'Lumina' },
  { icon: ScanLine, label: 'Lens OCR', rate: '1 credit / page', color: 'bg-amber-50 text-amber-600', app: 'All Apps' },
  { icon: BarChart3, label: 'Claims Scrubbing', rate: '0.5 credits / claim', color: 'bg-emerald-50 text-emerald-600', app: 'RevQ' },
  { icon: FileText, label: 'Denial Management', rate: '1 credit / appeal', color: 'bg-emerald-50 text-emerald-600', app: 'RevQ' },
  { icon: Sparkles, label: 'Revenue Forecasting', rate: '5 credits / report', color: 'bg-emerald-50 text-emerald-600', app: 'RevQ' },
  { icon: Calendar, label: 'Smart Scheduling', rate: '0.25 credits / slot', color: 'bg-sky-50 text-sky-600', app: 'Echo' },
  { icon: Phone, label: 'AI Phone Agent', rate: '2 credits / call', color: 'bg-sky-50 text-sky-600', app: 'Echo' },
  { icon: RefreshCw, label: 'Patient Flow Tracking', rate: '0.1 credits / event', color: 'bg-sky-50 text-sky-600', app: 'Echo' },
];

const Disclaimer = () => (
  <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="setup-disclaimer">
    <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
    <p className="text-[11px] text-amber-700 leading-relaxed">
      <span className="font-semibold">Simulated setup.</span> This is a demo walkthrough — not a real account setup. Please do not enter any personal or sensitive information.
    </p>
  </div>
);

const SetupWizardPage = () => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const { state } = useLocation();
  const selectedApps = state?.apps || ['lumina'];
  const [step, setStep] = useState(0);
  const [setupMethod, setSetupMethod] = useState(null);

  // Manual form state
  const [practiceName, setPracticeName] = useState('');
  const [npi, setNpi] = useState('');
  const [taxId, setTaxId] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [physicianTaxId, setPhysicianTaxId] = useState('');
  const [providers, setProviders] = useState([{ name: '', specialty: '', npi: '' }]);

  // Lens upload state
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [processedResults, setProcessedResults] = useState({});
  const [uploadLightbox, setUploadLightbox] = useState(null);
  const [showCsvAlts, setShowCsvAlts] = useState(false);

  // Billing
  const [billingCredits, setBillingCredits] = useState(100);

  // Team members
  const [teamMembers, setTeamMembers] = useState([
    { name: '', email: '', lumina: 'provider', echo: 'front_desk', revq: 'manager' },
  ]);
  const addTeamMember = () => setTeamMembers([...teamMembers, { name: '', email: '', lumina: 'provider', echo: 'front_desk', revq: 'manager' }]);
  const removeTeamMember = (i) => setTeamMembers(teamMembers.filter((_, idx) => idx !== i));
  const updateTeamMember = (i, field, val) => {
    const next = [...teamMembers];
    next[i] = { ...next[i], [field]: val };
    setTeamMembers(next);
  };

  const addProvider = () => setProviders([...providers, { name: '', specialty: '', npi: '' }]);
  const removeProvider = (i) => setProviders(providers.filter((_, idx) => idx !== i));
  const updateProvider = (i, field, val) => {
    const next = [...providers];
    next[i] = { ...next[i], [field]: val };
    setProviders(next);
  };

  const handleFileUploaded = (docId, file) => {
    setUploadedFiles(prev => ({ ...prev, [docId]: file }));
    setProcessedResults(prev => ({ ...prev, [docId]: LENS_RESULTS[docId] || LENS_RESULTS.patient_csv }));
  };

  // Find overlapping uploads
  const getSharedApps = (docId, appKey) => {
    const appDocs = APP_UPLOAD_DOCS[appKey];
    const doc = appDocs?.docs.find(d => d.id === docId);
    if (!doc?.shared?.length) return [];
    return doc.shared.filter(a => selectedApps.includes(a));
  };

  // Calculate overall setup progress
  const getSetupProgress = () => {
    let totalItems = 0;
    let completedItems = 0;
    if (setupMethod) { totalItems++; completedItems++; } else { totalItems++; }
    if (setupMethod === 'manual') {
      totalItems += 3; // practice, providers, billing
      if (practiceName && npi) completedItems++;
      if (providers.some(p => p.name)) completedItems++;
      if (billingCredits > 0) completedItems++;
    } else if (setupMethod === 'lens') {
      // Count all docs across selected apps
      selectedApps.forEach(appKey => {
        const appConfig = APP_UPLOAD_DOCS[appKey];
        if (appConfig) {
          appConfig.docs.forEach(doc => { totalItems++; if (uploadedFiles[doc.id]) completedItems++; });
          if (appConfig.csvAlternatives) {
            appConfig.csvAlternatives.docs.forEach(doc => { totalItems++; if (uploadedFiles[doc.id]) completedItems++; });
          }
        }
      });
      totalItems++; // billing
      if (billingCredits > 0) completedItems++;
    }
    return totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  };

  const setupProgress = getSetupProgress();

  const steps = setupMethod === 'manual'
    ? [{ label: 'Method', icon: Zap }, { label: 'Practice', icon: Building2 }, { label: 'Providers', icon: Users }, { label: 'Team', icon: Users }, { label: 'Billing', icon: CreditCard }, { label: 'Review', icon: Check }]
    : [{ label: 'Method', icon: Zap }, { label: 'Upload', icon: Upload }, { label: 'Team', icon: Users }, { label: 'Billing', icon: CreditCard }, { label: 'Review', icon: Check }];

  const handleNext = () => {
    if (step < steps.length - 1) setStep(step + 1);
    else {
      // Save setup progress to localStorage for incomplete setup prompts
      localStorage.setItem('ariaone-setup-progress', JSON.stringify({ progress: setupProgress, completedAt: setupProgress === 100 ? new Date().toISOString() : null, uploadedFiles: Object.keys(uploadedFiles) }));
      navigate('/setup-success', { state: { apps: selectedApps } });
    }
  };
  const handleBack = () => { if (step > 0) setStep(step - 1); };

  const renderDocRow = (doc, appKey) => {
    const Icon = doc.icon;
    const isUploaded = uploadedFiles[doc.id];
    const result = processedResults[doc.id];
    const shared = getSharedApps(doc.id, appKey);
    return (
      <div key={doc.id} className={cn("p-3 rounded-xl border transition-all",
        result ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200 hover:border-slate-300")}
        data-testid={`doc-${doc.id}`}>
        <div className="flex items-start gap-3">
          <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center shrink-0",
            result ? "bg-emerald-100" : "bg-slate-100")}>
            {result ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Icon className="h-4 w-4 text-slate-500" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium text-slate-800">{doc.label}</p>
              {doc.required && <Badge className="text-[8px] bg-red-50 text-red-600 border-0">Required</Badge>}
              {shared.length > 0 && <Badge className="text-[8px] bg-blue-50 text-blue-600 border-0">Shared with {shared.map(a => a.charAt(0).toUpperCase() + a.slice(1)).join(', ')}</Badge>}
            </div>
            <p className="text-xs text-slate-400 mt-0.5">{doc.desc}</p>
            {doc.extraDesc && <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed bg-slate-50 rounded-lg p-2 border border-slate-100">{doc.extraDesc}</p>}
            {isUploaded && (
              <div className="flex items-center gap-2 mt-2">
                <FileText className="h-3 w-3 text-emerald-500" />
                <span className="text-[11px] text-emerald-700 font-medium">{uploadedFiles[doc.id]?.name || 'File uploaded'}</span>
              </div>
            )}
            {result && (
              <div className="mt-2 space-y-1">
                {result.fields.slice(0, 3).map(r => (
                  <div key={r.f} className="flex items-center justify-between text-[11px]">
                    <span className="text-slate-500">{r.f}</span>
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-slate-700">{r.v}</span>
                      <span className={cn("px-1 py-0.5 rounded text-[9px] font-medium", r.c >= 95 ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600")}>{r.c}%</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="shrink-0">
            {result ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            ) : (
              <Button variant="outline" size="sm" className="h-7 text-[11px] gap-1 border-slate-200"
                onClick={() => setUploadLightbox({ docId: doc.id, label: doc.label })} data-testid={`upload-${doc.id}`}>
                <Upload className="h-3 w-3" /> Upload
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#fafafa] flex items-center justify-center p-4" data-testid="setup-wizard-page">
      <div className="w-full max-w-[680px] space-y-5">
        {/* Logo */}
        <div className="text-center">
          <img src={ARIAONE_LOGO} alt="AriaOne" className="h-8 mx-auto mb-2" data-testid="wizard-logo" />
          <p className="text-xs text-slate-400 tracking-wider font-medium">ACCOUNT SETUP</p>
        </div>

        {/* Selected apps */}
        <div className="flex items-center justify-center gap-2">
          {selectedApps.map(a => (
            <div key={a} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200">
              <img src={APP_LOGOS[a]} alt={a} className="h-3.5 w-auto" />
            </div>
          ))}
        </div>

        {/* Overall progress tracker */}
        <div className="bg-white rounded-xl border border-slate-200 px-5 py-3" data-testid="setup-progress-tracker">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-600">Setup progress</span>
            <span className={cn("text-xs font-semibold", setupProgress === 100 ? "text-emerald-600" : "text-slate-800")}>{setupProgress}%</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className={cn("h-full rounded-full transition-all duration-500", setupProgress === 100 ? "bg-emerald-500" : "bg-slate-900")} style={{ width: `${setupProgress}%` }} />
          </div>
        </div>

        {/* Step progress */}
        <div className="flex items-center justify-center gap-1" data-testid="wizard-progress">
          {steps.map((s, i) => {
            const Icon = s.icon;
            const isDone = i < step;
            const isCurrent = i === step;
            return (
              <div key={i} className="flex items-center">
                <div className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all",
                  isDone ? "bg-emerald-50 text-emerald-700" : isCurrent ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-400")}
                  data-testid={`wizard-step-${i}`}>
                  {isDone ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                  <span className="hidden sm:inline">{s.label}</span>
                </div>
                {i < steps.length - 1 && <div className={cn("w-6 h-px mx-1", isDone ? "bg-emerald-300" : "bg-slate-200")} />}
              </div>
            );
          })}
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 space-y-5">
          <Disclaimer />

          {/* ══ Step 0: Choose Method ══ */}
          {step === 0 && (
            <div className="space-y-4" data-testid="step-method">
              <div>
                <h2 className="text-lg font-bold text-slate-900">How would you like to set up?</h2>
                <p className="text-sm text-slate-500 mt-1">Choose manual entry or scan documents and let Lens do the work.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setSetupMethod('manual')}
                  className={cn("p-5 rounded-xl border-2 text-left transition-all",
                    setupMethod === 'manual' ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300")}
                  data-testid="method-manual">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center mb-3"><Building2 className="h-5 w-5 text-slate-600" /></div>
                  <p className="text-sm font-bold text-slate-900">Manual Setup</p>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">Type in your practice details, providers, and billing information step by step.</p>
                </button>
                <button onClick={() => setSetupMethod('lens')}
                  className={cn("p-5 rounded-xl border-2 text-left transition-all",
                    setupMethod === 'lens' ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300")}
                  data-testid="method-lens">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center mb-3"><ScanLine className="h-5 w-5 text-slate-600" /></div>
                  <p className="text-sm font-bold text-slate-900">Lens Upload</p>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">Upload existing files (EDI batches, CSVs, CCDAs) and Lens extracts everything automatically.</p>
                </button>
              </div>
            </div>
          )}

          {/* ══ Manual: Step 1 — Practice Info ══ */}
          {step === 1 && setupMethod === 'manual' && (
            <div className="space-y-4" data-testid="step-practice-manual">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Practice Information</h2>
                <p className="text-sm text-slate-500 mt-1">Enter your practice details</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><label className="text-[12px] text-slate-500 mb-1 block">Practice Name</label><Input value={practiceName} onChange={e => setPracticeName(e.target.value)} placeholder="Riverside Medical Group" className="h-10 text-sm border-slate-200" data-testid="input-practice-name" /></div>
                <div><label className="text-[12px] text-slate-500 mb-1 block">NPI Number</label><Input value={npi} onChange={e => setNpi(e.target.value)} placeholder="1234567890" className="h-10 text-sm border-slate-200" data-testid="input-npi" /></div>
                <div><label className="text-[12px] text-slate-500 mb-1 block">Practice Tax ID (EIN)</label><Input value={taxId} onChange={e => setTaxId(e.target.value)} placeholder="12-3456789" className="h-10 text-sm border-slate-200" data-testid="input-tax-id" /></div>
                <div><label className="text-[12px] text-slate-500 mb-1 block">Primary Specialty</label><Input value={specialty} onChange={e => setSpecialty(e.target.value)} placeholder="Internal Medicine" className="h-10 text-sm border-slate-200" data-testid="input-specialty" /></div>
                <div><label className="text-[12px] text-slate-500 mb-1 block">Physician Tax ID</label><Input value={physicianTaxId} onChange={e => setPhysicianTaxId(e.target.value)} placeholder="98-7654321" className="h-10 text-sm border-slate-200" data-testid="input-physician-tax-id" /></div>
                <div className="col-span-2"><label className="text-[12px] text-slate-500 mb-1 block">Practice Address</label><Input value={address} onChange={e => setAddress(e.target.value)} placeholder="2400 Riverside Dr, Suite 300, Austin TX 78704" className="h-10 text-sm border-slate-200" data-testid="input-address" /></div>
                <div><label className="text-[12px] text-slate-500 mb-1 block">Phone Number</label><Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="(512) 555-0190" className="h-10 text-sm border-slate-200" data-testid="input-phone" /></div>
              </div>
            </div>
          )}

          {/* ══ Manual: Step 2 — Providers ══ */}
          {step === 2 && setupMethod === 'manual' && (
            <div className="space-y-4" data-testid="step-providers-manual">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Add Providers</h2>
                <p className="text-sm text-slate-500 mt-1">List each provider in your practice</p>
              </div>
              <div className="space-y-3">
                {providers.map((p, i) => (
                  <div key={i} className="p-3 rounded-xl border border-slate-200 space-y-2" data-testid={`provider-row-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Provider {i + 1}</span>
                      {providers.length > 1 && <button onClick={() => removeProvider(i)} className="text-slate-400 hover:text-red-500 transition-colors" data-testid={`remove-provider-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <Input value={p.name} onChange={e => updateProvider(i, 'name', e.target.value)} placeholder="Dr. Sarah Chen" className="h-9 text-xs border-slate-200" data-testid={`provider-name-${i}`} />
                      <Input value={p.specialty} onChange={e => updateProvider(i, 'specialty', e.target.value)} placeholder="Internal Medicine" className="h-9 text-xs border-slate-200" data-testid={`provider-specialty-${i}`} />
                      <Input value={p.npi} onChange={e => updateProvider(i, 'npi', e.target.value)} placeholder="NPI #" className="h-9 text-xs border-slate-200" data-testid={`provider-npi-${i}`} />
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" onClick={addProvider} className="w-full h-9 text-xs border-dashed border-slate-300 text-slate-500 gap-1" data-testid="add-provider-btn">
                <Plus className="h-3 w-3" /> Add another provider
              </Button>
            </div>
          )}

          {/* ══ Lens: Step 1 — Upload Documents ══ */}
          {step === 1 && setupMethod === 'lens' && (
            <div className="space-y-5" data-testid="step-lens-upload">
              <div>
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <ScanLine className="h-5 w-5 text-slate-700" /> Upload Documents
                </h2>
                <p className="text-sm text-slate-500 mt-1">Upload files for each app. Lens will extract and configure your account automatically.</p>
              </div>

              {selectedApps.map(appKey => {
                const appConfig = APP_UPLOAD_DOCS[appKey];
                if (!appConfig) return null;
                return (
                  <div key={appKey} className="space-y-2" data-testid={`lens-section-${appKey}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <img src={appConfig.logo} alt={appConfig.name} className="h-4 w-auto" />
                      <span className="text-xs text-slate-400">documents</span>
                    </div>
                    <div className="space-y-2">
                      {appConfig.docs.map(doc => renderDocRow(doc, appKey))}
                    </div>

                    {/* RevQ CSV alternatives */}
                    {appConfig.csvAlternatives && (
                      <div className="mt-3">
                        <button
                          onClick={() => setShowCsvAlts(!showCsvAlts)}
                          className="flex items-center gap-2 text-left w-full p-3 rounded-xl border border-dashed border-slate-300 bg-slate-50/50 hover:bg-slate-50 transition-colors"
                          data-testid="csv-alternatives-toggle"
                        >
                          <Info className="h-4 w-4 text-slate-400 shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-slate-700">{appConfig.csvAlternatives.label}</p>
                            <p className="text-[11px] text-slate-500 mt-0.5">{appConfig.csvAlternatives.desc}</p>
                          </div>
                          <ArrowRight className={cn("h-4 w-4 text-slate-400 transition-transform", showCsvAlts && "rotate-90")} />
                        </button>
                        {showCsvAlts && (
                          <div className="space-y-2 mt-2 pl-2 border-l-2 border-slate-200">
                            {appConfig.csvAlternatives.docs.map(doc => renderDocRow(doc, appKey))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ══ Team Step — Add Users & Roles ══ */}
          {((step === 3 && setupMethod === 'manual') || (step === 2 && setupMethod === 'lens')) && (
            <div className="space-y-4" data-testid="step-team">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Team Members & Roles</h2>
                <p className="text-sm text-slate-500 mt-1">Add your team and assign their roles for each app. Each user's dashboard will adjust to their permissions.</p>
              </div>
              <div className="space-y-3">
                {teamMembers.map((m, i) => (
                  <div key={i} className="p-4 rounded-xl border border-slate-200 space-y-3" data-testid={`team-member-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Team member {i + 1}</span>
                      {teamMembers.length > 1 && (
                        <button onClick={() => removeTeamMember(i)} className="text-slate-400 hover:text-red-500 transition-colors" data-testid={`remove-member-${i}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={m.name} onChange={e => updateTeamMember(i, 'name', e.target.value)} placeholder="Full name" className="h-9 text-xs border-slate-200" data-testid={`member-name-${i}`} />
                      <Input value={m.email} onChange={e => updateTeamMember(i, 'email', e.target.value)} placeholder="email@practice.com" className="h-9 text-xs border-slate-200" data-testid={`member-email-${i}`} />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {selectedApps.includes('lumina') && (
                        <div className="flex-1 min-w-[130px]">
                          <label className="text-[10px] text-slate-400 mb-1 block">Lumina role</label>
                          <select value={m.lumina} onChange={e => updateTeamMember(i, 'lumina', e.target.value)}
                            className="w-full h-8 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid={`member-lumina-role-${i}`}>
                            <option value="provider">Provider</option>
                            <option value="ma">Medical Assistant (MA)</option>
                            <option value="rn">Registered Nurse (RN)</option>
                            <option value="nurse">LPN / Nurse</option>
                            <option value="admin">Admin</option>
                          </select>
                        </div>
                      )}
                      {selectedApps.includes('echo') && (
                        <div className="flex-1 min-w-[130px]">
                          <label className="text-[10px] text-slate-400 mb-1 block">Echo role</label>
                          <select value={m.echo} onChange={e => updateTeamMember(i, 'echo', e.target.value)}
                            className="w-full h-8 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid={`member-echo-role-${i}`}>
                            <option value="front_desk">Front Desk</option>
                            <option value="provider">Provider</option>
                            <option value="admin">Admin</option>
                          </select>
                        </div>
                      )}
                      {selectedApps.includes('revq') && (
                        <div className="flex-1 min-w-[130px]">
                          <label className="text-[10px] text-slate-400 mb-1 block">RevQ role</label>
                          <select value={m.revq} onChange={e => updateTeamMember(i, 'revq', e.target.value)}
                            className="w-full h-8 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid={`member-revq-role-${i}`}>
                            <option value="manager">Practice Manager</option>
                            <option value="biller">Biller / Coder</option>
                            <option value="provider">Provider</option>
                            <option value="admin">Admin</option>
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" onClick={addTeamMember} className="w-full h-9 text-xs border-dashed border-slate-300 text-slate-500 gap-1" data-testid="add-member-btn">
                <Plus className="h-3 w-3" /> Add another team member
              </Button>
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Each user will see a dashboard tailored to their role. Providers see clinical workflows, MAs see pre-visit prep, Billers see claims and denials.
                </p>
              </div>
            </div>
          )}

          {/* ══ Billing — Credits & Usage ══ */}
          {((step === 4 && setupMethod === 'manual') || (step === 3 && setupMethod === 'lens')) && (
            <div className="space-y-5" data-testid="step-billing">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Credits & Billing</h2>
                <p className="text-sm text-slate-500 mt-1">All Aria One services use a unified credit system. <span className="font-semibold text-slate-700">1 Credit = $1.00</span></p>
              </div>
              <div className="space-y-2">
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Credit pricing</p>
                <div className="grid grid-cols-3 gap-2">
                  {CREDIT_PRICING.filter(p => {
                    if (p.app === 'All Apps') return true;
                    if (p.app === 'Lumina') return selectedApps.includes('lumina');
                    if (p.app === 'RevQ') return selectedApps.includes('revq');
                    if (p.app === 'Echo') return selectedApps.includes('echo');
                    return false;
                  }).map(p => {
                    const Icon = p.icon;
                    return (
                      <div key={p.label} className="flex items-center gap-2.5 p-2.5 rounded-lg border border-slate-200" data-testid={`pricing-${p.label.replace(/\s/g, '-').toLowerCase()}`}>
                        <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center shrink-0", p.color.split(' ')[0])}><Icon className={cn("h-3.5 w-3.5", p.color.split(' ')[1])} /></div>
                        <div className="min-w-0"><p className="text-[11px] font-semibold text-slate-800 truncate">{p.label}</p><p className="text-[10px] text-slate-400">{p.rate}</p></div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="p-4 rounded-xl border border-slate-200">
                <p className="text-sm font-semibold text-slate-800 mb-3">Add credits to get started</p>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {[50, 100, 200, 500].map(amt => (
                    <button key={amt} onClick={() => setBillingCredits(amt)}
                      className={cn("p-2.5 rounded-xl border-2 text-center transition-all", billingCredits === amt ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300")}
                      data-testid={`billing-quick-${amt}`}>
                      <p className="text-base font-bold text-slate-900">{amt}</p><p className="text-[10px] text-slate-400">${amt}</p>
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">Custom:</span>
                  <div className="relative flex-1 max-w-[140px]">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                    <Input type="number" placeholder="0" className="h-9 text-sm pl-6 border-slate-200"
                      value={billingCredits && ![50,100,200,500].includes(billingCredits) ? billingCredits : ''}
                      onChange={e => setBillingCredits(Number(e.target.value) || 0)} data-testid="billing-custom-input" />
                  </div>
                  {billingCredits > 0 && <Badge className="bg-slate-100 text-slate-700 border-0 text-[10px]">{billingCredits} credits</Badge>}
                </div>
              </div>
              <div className="p-3.5 rounded-xl border border-dashed border-slate-300 bg-slate-50/50 flex items-start gap-3">
                <Info className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                <div><p className="text-xs font-semibold text-slate-700">Subscription pricing available</p><p className="text-[11px] text-slate-500 mt-0.5">At 500+ credits/month, flat-rate subscription pricing unlocks automatically — save up to 20%.</p></div>
              </div>
              <div className="p-4 rounded-xl border border-slate-200">
                <p className="text-sm font-semibold text-slate-800 mb-3">Payment method</p>
                <Input placeholder="4242 4242 4242 4242" className="h-10 text-sm border-slate-200 mb-3" data-testid="input-card" />
                <div className="grid grid-cols-2 gap-3"><Input placeholder="MM / YY" className="h-10 text-sm border-slate-200" /><Input placeholder="CVC" className="h-10 text-sm border-slate-200" /></div>
              </div>
            </div>
          )}

          {/* ══ Review ══ */}
          {step === steps.length - 1 && (
            <div className="space-y-3" data-testid="step-review">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Review & Launch</h2>
                <p className="text-sm text-slate-500 mt-1">Everything looks good. Ready to go!</p>
              </div>
              <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/50">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Setup Method</p>
                <p className="text-sm font-medium text-slate-800">{setupMethod === 'manual' ? 'Manual Entry' : 'Lens Upload'}</p>
              </div>
              {setupMethod === 'manual' && practiceName && (
                <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/50">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Practice</p>
                  <p className="text-sm font-medium text-slate-800">{practiceName}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{[npi && `NPI: ${npi}`, taxId && `Tax ID: ${taxId}`, specialty].filter(Boolean).join(' · ')}</p>
                </div>
              )}
              {setupMethod === 'lens' && Object.keys(processedResults).length > 0 && (
                <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/50">
                  <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider mb-1.5">Lens Upload</p>
                  <p className="text-sm text-emerald-700">{Object.keys(processedResults).length} document(s) processed successfully</p>
                  <div className="mt-2 space-y-1">
                    {Object.entries(uploadedFiles).map(([id, file]) => (
                      <div key={id} className="flex items-center gap-2 text-[11px] text-emerald-600"><CheckCircle2 className="h-3 w-3" />{file?.name || id}</div>
                    ))}
                  </div>
                </div>
              )}
              <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/50">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Applications</p>
                <div className="flex flex-wrap gap-2">
                  {selectedApps.map(a => (<div key={a} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-slate-200"><img src={APP_LOGOS[a]} alt={a} className="h-3.5 w-auto" /></div>))}
                </div>
              </div>
              {setupMethod === 'manual' && providers.filter(p => p.name).length > 0 && (
                <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/50">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Providers</p>
                  <p className="text-sm text-slate-700">{providers.filter(p => p.name).length} provider(s) configured</p>
                </div>
              )}
              {teamMembers.filter(m => m.name).length > 0 && (
                <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/50">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Team</p>
                  <p className="text-sm text-slate-700">{teamMembers.filter(m => m.name).length} team member(s) configured</p>
                  <div className="mt-2 space-y-1">
                    {teamMembers.filter(m => m.name).map((m, i) => (
                      <div key={i} className="flex items-center gap-2 text-[11px] text-slate-600">
                        <CheckCircle2 className="h-3 w-3 text-slate-400" />
                        <span className="font-medium">{m.name}</span>
                        <span className="text-slate-400">{m.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/50">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Credits & Billing</p>
                <p className="text-sm text-slate-700">{billingCredits} initial credits (${billingCredits}.00) · Pay-as-you-go</p>
              </div>
              {/* Incomplete setup warning */}
              {setupProgress < 100 && (
                <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="incomplete-setup-warning">
                  <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-amber-700">Setup is {setupProgress}% complete</p>
                    <p className="text-[11px] text-amber-600 mt-0.5">You can finish the remaining steps later from Settings → Account Setup in any app.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center gap-3 pt-5 border-t border-slate-100">
            {step > 0 && (
              <Button variant="outline" onClick={handleBack} className="h-10 px-5 text-sm border-slate-200 text-slate-600" data-testid="wizard-back-btn">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
            <div className="flex-1" />
            {((step === 1 && setupMethod === 'lens') || steps[step]?.label === 'Team') && (
              <Button variant="ghost" onClick={handleNext} className="h-10 px-5 text-sm text-slate-500" data-testid="wizard-skip-btn">
                Skip for now
              </Button>
            )}
            <Button onClick={handleNext}
              disabled={step === 0 && !setupMethod}
              className={cn("h-10 px-6 text-sm font-medium",
                step === 0 && !setupMethod ? "bg-slate-200 text-slate-400 cursor-not-allowed" : "bg-slate-900 hover:bg-slate-800 text-white")}
              data-testid="wizard-next-btn">
              {step === steps.length - 1 ? 'Launch my account' : 'Continue'} <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      {/* Upload Lightbox */}
      <SetupUploadLightbox
        open={!!uploadLightbox}
        onOpenChange={v => { if (!v) setUploadLightbox(null); }}
        docId={uploadLightbox?.docId}
        docLabel={uploadLightbox?.label}
        onFileSelected={(file) => handleFileUploaded(uploadLightbox?.docId, file)}
      />
    </div>
  );
};

export default SetupWizardPage;
